import { useEffect, useState } from 'react';
import {
  Badge, Button, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { fetchTicket } from '../api/drilldown';
import { downloadInvoiceFile } from '../api/invoiceFile';
import { t } from '../lib/i18n';
import type { TicketDetail } from '../api/types';
import CFDIViewer from './CFDIViewer';
import DrillCell from './DrillCell';
import IconButton from './IconButton';
import Modal from './Modal';
import { BackIcon, CloseIcon } from './icons';

interface Props {
  sid: number;
  tnum: number;
  /**
   * When true, render CFDI XML + PDF download buttons in the header. The
   * caller decides based on the current user's role (staff: false; everyone
   * else: true). Backend also enforces — this is just SPA hygiene.
   */
  canDownloadInvoice?: boolean;
  onClose: () => void;
  /** Supplied by DrilldownHost when this modal sits on top of another (JCD-59). */
  onBack?: () => void;
}

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

function timeFromSecs(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default function TicketDetailModal({ sid, tnum, canDownloadInvoice, onClose, onBack }: Props) {
  const [data, setData] = useState<TicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dlErr, setDlErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<'xml' | 'pdf' | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchTicket(sid, tnum)
      .then((d) => { if (alive) setData(d); })
      .catch((e: Error) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [sid, tnum]);

  const isCredit = data ? (data.header.type === 0 || data.header.type === 1) : false;
  const isDebit  = data ? (data.header.type === 1 || data.header.type === 3) : false;

  return (
    <>
    <Modal
      onClose={onClose}
      ariaLabel={`${t.ticket.title} #${tnum}`}
      role="dialog"
      panelClassName="max-w-5xl my-8 p-6"
    >
      <div className="flex items-start gap-4">
        {onBack && (
          <IconButton onClick={onBack} label={t.drilldown.back} size="sm">
            <BackIcon />
          </IconButton>
        )}
        <Title>
          {t.ticket.title} #{tnum}
          <span className="text-tremor-content text-base font-normal ml-2">
            ({t.ticket.store} {sid})
          </span>
        </Title>
        {canDownloadInvoice && (
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="xs"
              variant="primary"
              disabled={busy !== null || !data}
              onClick={() => setViewerOpen(true)}
            >
              {t.ticket.download.view}
            </Button>
            <Button
              size="xs"
              variant="secondary"
              loading={busy === 'xml'}
              disabled={busy !== null || !data}
              onClick={async () => {
                setDlErr(null);
                setBusy('xml');
                try {
                  await downloadInvoiceFile('xml', sid, tnum, data?.header.idate);
                } catch (e) {
                  setDlErr((e as Error).message);
                } finally {
                  setBusy(null);
                }
              }}
            >
              {t.ticket.download.xml}
            </Button>
            <Button
              size="xs"
              variant="secondary"
              loading={busy === 'pdf'}
              disabled={busy !== null || !data}
              onClick={async () => {
                setDlErr(null);
                setBusy('pdf');
                try {
                  await downloadInvoiceFile('pdf', sid, tnum);
                } catch (e) {
                  setDlErr((e as Error).message);
                } finally {
                  setBusy(null);
                }
              }}
            >
              {t.ticket.download.pdf}
            </Button>
          </div>
        )}
        <IconButton
          label={t.common.close}
          onClick={onClose}
          className={canDownloadInvoice ? '' : 'ml-auto'}
        >
          <CloseIcon />
        </IconButton>
      </div>
      {dlErr && (
        <div className="rounded-tremor-default bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200 px-3 py-2 mt-2 text-sm">
          {dlErr}
        </div>
      )}

      {error && (
        <div className="rounded-tremor-default bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200 p-4 mt-4">
          {error}
        </div>
      )}
      {!data && !error && <Text className="mt-4">{t.common.loading}</Text>}

      {data && (
        <>
          <Card className="mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field label={t.ticket.date} value={`${data.header.idate} ${timeFromSecs(data.header.itime_secs)}`} />
              <Field
                label={t.ticket.account}
                value={
                  isCredit ? (
                    <DrillCell target={data.header.anum > 0 ? { kind: 'account', anum: data.header.anum } : null}>
                      {data.header.anum} {data.header.name}
                    </DrillCell>
                  ) : (
                    <>{data.header.anum} {data.header.name}</>
                  )
                }
              />
              <Field label={t.ticket.store} value={`${data.header.sid} ${data.header.store_name}`} />
              <Field label={t.ticket.salesperson} value={data.header.slid_name || data.header.slid || '—'} />
              <Field label={t.ticket.invoice} value={data.header.inum || '—'} />
              <Field
                label={t.ticket.type}
                value={
                  <Badge color={isCredit ? 'amber' : 'emerald'}>
                    {isCredit ? t.sales.badge.credit : t.sales.badge.cash}
                    {isDebit ? ` (${t.sales.badge.debit})` : ''}
                  </Badge>
                }
              />
              <Field label={t.ticket.subt}  value={fmtMxn.format(data.header.subt_mxn)} />
              <Field label={t.ticket.tax}   value={fmtMxn.format(data.header.tax_mxn)} />
              <Field label={t.ticket.total} value={<span className="font-semibold">{fmtMxn.format(data.header.total_mxn)}</span>} />
              {isCredit && (
                <>
                  <Field label={t.ticket.paid}    value={fmtMxn.format(data.header.paid_mxn ?? 0)} />
                  <Field
                    label={t.ticket.balance}
                    value={
                      (data.header.balance_mxn ?? 0) > 0 ? (
                        <span className="text-red-600 dark:text-red-400 font-semibold">{fmtMxn.format(data.header.balance_mxn ?? 0)}</span>
                      ) : (
                        <Badge color="green">{t.sales.badge.paid}</Badge>
                      )
                    }
                  />
                </>
              )}
            </div>
          </Card>

          <Title className="mt-6 text-base">{t.ticket.lines} ({data.lines.length})</Title>
          <Card className="p-0 overflow-hidden mt-2">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.ticket.col.line}</TableHeaderCell>
                  <TableHeaderCell>{t.ticket.col.part}</TableHeaderCell>
                  <TableHeaderCell>{t.ticket.col.pdesc}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.ticket.col.qty}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.ticket.col.sell}</TableHeaderCell>
                  {data.lines.some((l) => l.pcost_mxn !== undefined) && (
                    <TableHeaderCell className="text-right">{t.ticket.col.pcost}</TableHeaderCell>
                  )}
                  <TableHeaderCell className="text-right">{t.ticket.col.extended}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.lines.map((l, i) => (
                  <TableRow key={`${l.line}-${l.part}-${i}`}>
                    <TableCell className="font-mono text-xs">{l.line}</TableCell>
                    <TableCell className="font-mono">
                      {/* JCD-59: was /parts?q=<part> — a substring search on
                          the parts page that threw away both the line and
                          this ticket's store. The line and sid were right
                          here all along; drill the exact inv record. */}
                      <DrillCell
                        target={{ kind: 'inv', sid: data.header.sid, line: l.line, part: l.part }}
                      >
                        {l.part}
                      </DrillCell>
                    </TableCell>
                    <TableCell>{l.pdesc}</TableCell>
                    <TableCell className="text-right">{l.qty_sold}</TableCell>
                    <TableCell className="text-right">{fmtMxn.format(l.sell_mxn)}</TableCell>
                    {l.pcost_mxn !== undefined && (
                      <TableCell className="text-right">{fmtMxn.format(l.pcost_mxn)}</TableCell>
                    )}
                    <TableCell className="text-right">{fmtMxn.format(l.extended_mxn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {isCredit && (
            <>
              <Title className="mt-6 text-base">{t.ticket.payments} ({data.payments.length})</Title>
              {data.payments.length === 0 ? (
                <Text className="mt-2">{t.ticket.noPayments}</Text>
              ) : (
                <Card className="p-0 overflow-hidden mt-2">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>{t.ticket.col.tdate}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{t.ticket.col.amount}</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.payments.map((p, i) => (
                        <TableRow key={i}>
                          <TableCell>{p.tdate ?? '—'}</TableCell>
                          <TableCell className="text-right">{fmtMxn.format(p.amount_mxn)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </Modal>
    {viewerOpen && (
      <CFDIViewer
        sid={sid}
        tnum={tnum}
        idate={data?.header.idate}
        onClose={() => setViewerOpen(false)}
      />
    )}
    </>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong">{value}</div>
    </div>
  );
}
